import { ReactElement, ReactNode, useCallback, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Box, Divider, Grid, Menu, MenuItem, Skeleton } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import { makeStyles } from "tss-react/mui"

import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveUserId, selectEffectiveCompanyId } from "../../../../../../context/contextSelectors"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { CalendarEvent } from "../../../../../../../shared/components/calendar/calendarTypes"
import { ConfigErrorBoundary } from "../../../../../../../shared/components/createOrder/components/ConfigErrorBoundary"
import { CreateOrderType } from "../../../../../../../shared/components/createOrder/helpers"
import { useOrderDialogUrlState } from "../../../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { LockedDialog, useLockedDialogState } from "../../../../../../../shared/components/dialogs/LockedDialog"
import {
    StandardErrorDialog,
    useStandardErrorDialogState,
} from "../../../../../../../shared/components/dialogs/StandardErrorDialog"
import {
    StandardSuccessDialog,
    SuccessDialogType,
} from "../../../../../../../shared/components/dialogs/StandardSuccessDialog"
import { MeetingServiceOrder } from "../../../../../../merchant/merchant/production/meetingService/meetingServiceTypes"
import { UserDetails } from "../../../../../nonAdminUserApi"
import { UpcomingOrdersMeetingService } from "../../../../upcomingOrders/meetingService/meetingServiceApi"
import { MenuMeeting } from "../../../weekMenuTypes"
import { UserWeekMenu } from "../../../weekMenuApi"
import {
    fetchOrderDetails,
    MeetingOrderAction,
    OrderDetailsResponse,
    ordersEndBooking,
} from "../../../meetingServiceApi"
import { UserMeetingsReceipt } from "../../../../MeetingsReceipt/UserMeetingsReceipt"

import { ActionConfirmationDialog } from "./ActionConfirmationDialog"
import { ExtendBookingContent } from "./ExtendBookingContent"
import { UserDeleteMeetingDialog } from "./UserDeleteMeetingDialog"

type StyleProps = {
    hasTopPadding: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, { hasTopPadding }) => ({
    menu: {
        "& .MuiPaper-root": {
            padding: 0,
            minWidth: "10rem",
            width: "max-content",
            "& li:last-child": {
                marginBottom: "0.5rem",
            },
        },
        "& .MuiList-root": {
            padding: 0,
            paddingTop: hasTopPadding ? "0.5rem" : 0,
        },
        "& .MuiMenuItem-root": {
            whiteSpace: "normal",
            wordWrap: "break-word",
        },
    },
    updateModalContainer: {
        padding: "0 2rem 2rem 2rem",
    },
    deleteItem: {
        color: tailwindPalette.red[600],
    },
    skeletonCard: {
        height: "1.5rem",
        minWidth: "14rem",
        margin: ".5rem",
        borderRadius: ".625rem",
    },
    topComponentDivider: {
        marginBottom: "0.5rem",
    },
    hideEdge: { display: "none" },
    noActionsMenuItem: {
        padding: "0.5rem 1rem",
        whiteSpace: "nowrap",
    },
}))

const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)
export type ActionType = "edit_meeting" | "end_booking" | "duplicate_meeting_service"
type MenuVariant = "meeting-service" | "resource-booking"
type MenuActionKey = keyof MeetingOrderAction | "showReceipt" | "duplicateMeetingService" | "editMeeting"
export type MeetingCardOrderTypes = CalendarEvent

type MenuItemConfig = {
    key: MenuActionKey
    element: ReactElement
    dividerBefore?: boolean
}

export type AdditionalEventMenuItem = {
    label: string
    key: string
    onClick: (id: number) => void
}

type MeetingMenuCardProps = {
    ignoreMenuContainer?: boolean
    anchorEl?: HTMLElement | null
    handleMenuClose: () => void
    menuOnApi: boolean
    topComponent?: ReactNode
    meeting: MenuMeeting
    meetings?: MenuMeeting[] | MeetingServiceOrder[]
    variant?: MenuVariant
    additionalMenuItems?: AdditionalEventMenuItem[]
    orders?: MeetingCardOrderTypes[]
    onChatOpen?: (orderId: number, orderTitle: string) => void
}

export function MeetingCard({
    ignoreMenuContainer = false,
    anchorEl,
    handleMenuClose,
    meeting,
    meetings,
    menuOnApi,
    topComponent,
    additionalMenuItems,
    variant = "resource-booking",
    orders,
    onChatOpen,
}: MeetingMenuCardProps) {
    const { l10n } = useAppLocalization()
    const { openDialog } = useOrderDialogUrlState()
    const queryClient = useQueryClient()
    const userId = useAppSelector(selectEffectiveUserId)
    const companyId = useAppSelector(selectEffectiveCompanyId)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const { errorDialog: showErrorDialog, openErrorDialog, closeErrorDialog } = useStandardErrorDialogState()
    const [showExtendBookingConfirmModal, setShowExtendBookingConfirmModal] = useState(false)
    const [showReceiptModal, setShowReceiptModal] = useState(false)
    const { lockedDialog, openLockedDialog, closeLockedDialog: closeLockedDialogState } = useLockedDialogState()

    const [showSuccessModal, setShowSuccessModal] = useState<{
        open: boolean
        type?: SuccessDialogType
        closeCallback?: () => void
        deletionData?: any
    } | null>(null)
    const [confirmationDialog, setConfirmationDialog] = useState<{
        open: boolean
        actionType: ActionType
    } | null>(null)

    const { data: orderDetails, isLoading } = useApiQuery(fetchOrderDetails, {
        queryName: UserWeekMenu.USER_MEETING_SERVICE_DETAILS,
        dependencies: {
            orderId: meeting.id,
        },
        enabled: anchorEl === null ? Boolean(anchorEl) : menuOnApi,
        select: transformData,
        useErrorBoundary: true,
    })

    const { mutate: callEndBooking, isLoading: isEndingBooking } = useApiMutation(ordersEndBooking, {
        onSuccess: async () => {
            setConfirmationDialog(null)
            setShowSuccessModal({ open: true, type: "endBooking" })
        },
    })

    const orderAction = orderDetails?.orderAction ?? meeting.orderAction
    const { classes } = useStyles({ hasTopPadding: !topComponent && !additionalMenuItems?.length && !!orderAction })

    const editMeeting = () => {
        const { hasBooking, hasCatering } = orderDetails?.orderSummary ?? {}

        if (hasBooking && !hasCatering) {
            if (!orderAction?.updateBooking?.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.updateBooking?.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-update"),
                    body:
                        orderAction?.updateBooking?.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-update-booking-restriction-message"),
                })
            }
        }

        if (hasCatering && !hasBooking) {
            if (!orderAction?.updateMeetingService?.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.updateMeetingService?.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-update"),
                    body:
                        orderAction?.updateMeetingService?.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-update-meeting-restriction-message"),
                })
            }
        }

        setConfirmationDialog({
            open: true,
            actionType: "edit_meeting",
        })
    }

    const handleEditMeetingConfirm = () => {
        const { hasBooking, hasCatering } = orderDetails?.orderSummary ?? {}

        let startType: CreateOrderType = CreateOrderType.RESOURCE_BOOKING

        if (hasCatering && !hasBooking) {
            startType = CreateOrderType.MEETING_SERVICE
        }

        openDialog({
            orderId: meeting.id,
            mode: "edit",
            activeOrderTab: startType,
        })
    }

    const extendBooking = () => {
        if (orderAction?.extendBooking.allow) {
            setShowExtendBookingConfirmModal(true)
        } else {
            openLockedDialog({
                title:
                    orderAction?.extendBooking.message?.title ||
                    l10n.getString("user-home-canteen-upcoming-orders-extend-booking-item"),
                body:
                    orderAction?.extendBooking.message?.body ||
                    l10n.getString("user-home-canteen-upcoming-orders-extend-booking-restriction-message"),
            })
        }
    }

    const endBooking = () => {
        if (orderAction?.endBooking.allow) {
            setConfirmationDialog({
                open: true,
                actionType: "end_booking",
            })
        } else {
            openLockedDialog({
                title:
                    orderAction?.endBooking.message?.title ||
                    l10n.getString("user-home-canteen-upcoming-orders-end-booking-item"),
                body:
                    orderAction?.endBooking.message?.body ||
                    l10n.getString("user-home-canteen-upcoming-orders-end-booking-restriction-message"),
            })
        }
    }

    const handleEndBookingConfirm = () => {
        callEndBooking({
            orderId: meeting.id,
            endDateTime: meeting.meetingDetails.endDateTime,
        })
    }

    const duplicateMeetingService = () => {
        setConfirmationDialog({
            open: true,
            actionType: "duplicate_meeting_service",
        })
    }

    const handleDuplicateMeetingServiceConfirm = () => {
        openDialog({
            orderId: meeting.id,
            mode: "duplicate",
            activeOrderTab: CreateOrderType.MEETING_SERVICE,
        })
    }

    const deleteCallback = () => {
        handleMenuClose()
        queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.MEETING_SERVICE_LIST],
        })
    }

    const onDelete = () => {
        const { hasBooking, hasCatering } = orderDetails?.orderSummary ?? {}

        if (hasBooking && !hasCatering) {
            if (!orderAction?.deleteBooking.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.deleteBooking.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-delete-order"),
                    body:
                        orderAction?.deleteBooking.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-delete-order-restriction-message"),
                })
            }
            return setShowDeleteModal(true)
        }

        if (hasCatering && !hasBooking) {
            if (!orderAction?.deleteMeetingService.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.deleteMeetingService.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-delete-order"),
                    body:
                        orderAction?.deleteMeetingService.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-delete-order-restriction-message"),
                })
            }
            return setShowDeleteModal(true)
        }

        return setShowDeleteModal(true)
    }

    const handleShowReceipt = () => setShowReceiptModal(true)
    const handleCloseReceipt = () => {
        setShowReceiptModal(false)
        handleMenuClose()
    }

    const closeLockedDialog = () => {
        closeLockedDialogState()
        handleMenuClose()
    }

    const invalidateOrderQueries = useCallback(async () => {
        // Invalidate queries after deletion
        await queryClient.invalidateQueries({
            queryKey: [UserWeekMenu.USER_MEETING_ORDER_FREE_BUSY_SCHEDULE],
            refetchType: "all", // adding because react query does not refetch query.
        })
        // Invalidate calendar events to refresh the calendar after deletion
        await queryClient.invalidateQueries({
            queryKey: [UserDetails.USER_CALENDAR_EVENTS],
            refetchType: "all",
        })
        // Invalidate upcoming orders table to refresh after deletion
        await queryClient.invalidateQueries({
            queryKey: [UpcomingOrdersMeetingService.UPCOMING_ORDERS_MEETING_SERVICE],
            refetchType: "all",
        })
    }, [queryClient])

    const handleSuccessDialogClose = useCallback(async () => {
        showSuccessModal?.closeCallback?.()
        handleMenuClose()

        await invalidateOrderQueries()

        // Show snackbar if there's a service response message
        if (showSuccessModal?.deletionData?.serviceResponse?.displayMessage) {
            enqueueSnackbar({
                variant: "success",
                message: showSuccessModal.deletionData.serviceResponse.displayMessage,
            })
        }

        setShowSuccessModal({ open: false })
    }, [showSuccessModal, handleMenuClose, invalidateOrderQueries, enqueueSnackbar])

    const handleErrorDialogClose = useCallback(async () => {
        closeErrorDialog()
        handleMenuClose()

        await invalidateOrderQueries()
    }, [closeErrorDialog, handleMenuClose, invalidateOrderQueries])

    const closeConfirmationDialog = () => {
        setConfirmationDialog(null)
        handleMenuClose()
    }

    const handleConfirmAction = () => {
        if (!confirmationDialog) return

        switch (confirmationDialog.actionType) {
            case "edit_meeting":
                handleEditMeetingConfirm()
                break
            case "end_booking":
                handleEndBookingConfirm()
                break
            case "duplicate_meeting_service":
                handleDuplicateMeetingServiceConfirm()
                break
        }

        closeConfirmationDialog()
    }

    const customMenuItems = additionalMenuItems?.map(({ key, label, onClick }) => (
        <MenuItem key={key} onClick={() => onClick(meeting.id)}>
            {label}
        </MenuItem>
    ))

    const deleteAction =
        Boolean(orderAction?.deleteBooking) || Boolean(orderAction?.deleteMeetingService)
            ? [
                  <Divider key={`divider-top`} className={classes.topComponentDivider} />,
                  <LocalizedStrict id="user-home-canteen-upcoming-orders-delete-item">
                      <MenuItem className={classes.deleteItem} onClick={onDelete}>
                          Delete
                      </MenuItem>
                  </LocalizedStrict>,
              ]
            : []

    const renderMenuItems = (orderAction?: MeetingOrderAction) => {
        if (!orderAction) return []
        return menuItemsConfig
            .reduce<React.ReactNode[]>(
                (acc, { key, element, dividerBefore }, index) => {
                    const isShowReceipt = key === "showReceipt"
                    const isDuplicateMeetingService = key === "duplicateMeetingService"
                    const hasMeetingService = !!orderDetails?.deliveries && orderDetails?.deliveries.length > 0

                    if (isShowReceipt) {
                        acc.push(element)
                        return acc
                    }

                    if (isDuplicateMeetingService) {
                        if (hasMeetingService) {
                            acc.push(element)
                        }
                        return acc
                    }

                    const action = orderAction[key as keyof MeetingOrderAction]

                    if (key === "editMeeting") {
                        if (orderAction.updateBooking || orderAction.updateMeetingService) {
                            if (dividerBefore && acc.length > 0) acc.push(<Divider key={`divider-${index}`} />)
                            acc.push(element)
                        }
                        return acc
                    }

                    if (!action) return acc
                    if (dividerBefore && acc.length > 0) acc.push(<Divider key={`divider-${index}`} />)

                    acc.push(element)
                    return acc
                },
                [
                    topComponent,
                    Boolean(topComponent) && <Divider key={`divider-top`} className={classes.topComponentDivider} />,
                    customMenuItems,
                    <LocalizedStrict id="user-home-canteen-upcoming-orders-receipt-item">
                        <MenuItem onClick={handleShowReceipt}>Receipt</MenuItem>
                    </LocalizedStrict>,
                    ...(onChatOpen
                        ? [
                              <LocalizedStrict id="order-chat-menu-item">
                                  <MenuItem
                                      key="chat"
                                      onClick={() => {
                                          onChatOpen(meeting.id, meeting.meetingDetails.meetingName)
                                          handleMenuClose()
                                      }}
                                  >
                                      Order message
                                  </MenuItem>
                              </LocalizedStrict>,
                          ]
                        : []),
                ],
            )
            .concat(deleteAction)
    }

    const noActionsAvailable = [
        <LocalizedStrict
            id="user-home-canteen-news-category-no-actions"
            vars={{
                orderId: meeting.id.toString(),
            }}
        >
            <Box className={classes.noActionsMenuItem}>No actions available for order #{meeting.id.toString()}</Box>
        </LocalizedStrict>,
    ]

    const menuItemsConfig: MenuItemConfig[] = [
        {
            key: "editMeeting",
            element: (
                <LocalizedStrict id="user-home-canteen-upcoming-orders-edit-item">
                    <MenuItem onClick={editMeeting}>Edit</MenuItem>
                </LocalizedStrict>
            ),
            dividerBefore: true,
        },
        {
            key: "extendBooking",
            element: (
                <LocalizedStrict id="user-home-canteen-upcoming-orders-extend-booking-item">
                    <MenuItem onClick={extendBooking}>Extend booking</MenuItem>
                </LocalizedStrict>
            ),
        },
        {
            key: "endBooking",
            element: (
                <LocalizedStrict id="user-home-canteen-upcoming-orders-end-booking-item">
                    <MenuItem onClick={endBooking}>End booking</MenuItem>
                </LocalizedStrict>
            ),
        },
        {
            key: "duplicateMeetingService",
            element: (
                <LocalizedEnum
                    base="user-home-canteen-upcoming-orders-duplicate-meeting-service-item"
                    enumValue={variant}
                >
                    <MenuItem onClick={duplicateMeetingService}>Duplicate</MenuItem>
                </LocalizedEnum>
            ),
        },
    ]

    const loadingSkeletons = (
        <>
            <Skeleton variant="rectangular" className={classes.skeletonCard} />
        </>
    )

    const menuItems =
        isLoading && menuOnApi ? loadingSkeletons : orderAction ? renderMenuItems(orderAction) : noActionsAvailable

    return (
        <>
            {ignoreMenuContainer ? (
                menuItems
            ) : (
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    className={classes.menu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    {menuItems}
                </Menu>
            )}

            {showErrorDialog.open && (
                <StandardErrorDialog onClose={handleErrorDialogClose} dialogDetails={showErrorDialog} />
            )}

            {showDeleteModal && (
                <UserDeleteMeetingDialog
                    meeting={orderDetails ?? meeting}
                    open={showDeleteModal}
                    disabledDeleteMeetingService={orderAction?.deleteMeetingService?.message?.body}
                    disabledDeleteBooking={orderAction?.deleteBooking?.message?.body}
                    onSuccess={(data) => {
                        setShowDeleteModal(false)
                        const { hasCatering, hasBooking } = orderDetails?.orderSummary ?? meeting.orderSummary ?? {}
                        const isMeetingService = hasCatering && !hasBooking
                        setShowSuccessModal({
                            open: true,
                            type: isMeetingService ? "deleteOrder" : "deleteBooking",
                            closeCallback: deleteCallback,
                            deletionData: data,
                        })
                    }}
                    onError={(error) => {
                        setShowDeleteModal(false)
                        openErrorDialog(error)
                    }}
                    onClose={() => {
                        handleMenuClose()
                        setShowDeleteModal(false)
                    }}
                />
            )}

            {lockedDialog.open && <LockedDialog dialogDetails={lockedDialog} onClose={closeLockedDialog} />}

            {confirmationDialog && (
                <ActionConfirmationDialog
                    open={confirmationDialog.open}
                    onClose={closeConfirmationDialog}
                    onConfirm={handleConfirmAction}
                    actionType={confirmationDialog.actionType}
                    meeting={orderDetails ?? meeting}
                    isLoading={isEndingBooking}
                />
            )}

            {showExtendBookingConfirmModal && (
                <StandardDialog
                    open={showExtendBookingConfirmModal}
                    onClose={() => {
                        handleMenuClose()
                        setShowExtendBookingConfirmModal(false)
                    }}
                    maxWidthProp="sm"
                    dialogTitle={
                        <LocalizedStrict id="user-home-canteen-upcoming-orders-extend-modal-title">
                            <>Extend booking</>
                        </LocalizedStrict>
                    }
                >
                    <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        direction="column"
                        wrap="nowrap"
                        className={classes.updateModalContainer}
                    >
                        <ExtendBookingContent
                            meeting={meeting}
                            onConfirm={() => {
                                setShowSuccessModal({ open: true, type: "extendBooking" })
                                setShowExtendBookingConfirmModal(false)
                            }}
                        />
                    </Grid>
                </StandardDialog>
            )}

            {showSuccessModal?.open && (
                <StandardSuccessDialog
                    dialogType={showSuccessModal?.type}
                    open={showSuccessModal.open || true}
                    onClose={handleSuccessDialogClose}
                />
            )}

            {showReceiptModal && (
                <UserMeetingsReceipt
                    updateEdge={classes.hideEdge}
                    open={showReceiptModal}
                    id={meeting.id}
                    meetings={orders ?? meetings}
                    onClose={handleCloseReceipt}
                />
            )}
        </>
    )
}

export const MeetingCardMenu = (props: MeetingMenuCardProps) => (
    <ConfigErrorBoundary onClose={props?.handleMenuClose}>
        <MeetingCard {...props} />
    </ConfigErrorBoundary>
)
